import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Shield, CheckCircle } from "lucide-react";
import heroImage from "@/assets/hero-bilan.webp";
import { submitLead } from "@/lib/submitLead";

const PHONE_REGEX = /^(?:(?:\+33|0033|0)\s?[67])(?:[\s.-]?\d{2}){4}$/;
const EMAIL_REGEX = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

const HeroSection = () => {
  const navigate = useNavigate();
  const [form, setForm] = useState({ prenom: "", email: "", telephone: "" });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(false);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setForm({ ...form, [e.target.name]: e.target.value });
    setErrors({ ...errors, [e.target.name]: "" });
  };

  const validate = () => {
    const errs: Record<string, string> = {};
    if (!form.prenom.trim()) errs.prenom = "Prénom requis";
    if (!form.email.trim()) errs.email = "Email requis";
    else if (!EMAIL_REGEX.test(form.email.trim())) errs.email = "Email invalide";
    if (!form.telephone.trim()) errs.telephone = "Téléphone requis";
    else if (!PHONE_REGEX.test(form.telephone.trim().replace(/\s/g, ""))) errs.telephone = "Numéro français invalide (06, 07 ou +33)";
    return errs;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length > 0) { setErrors(errs); return; }
    setLoading(true);
    const code = Math.floor(100000 + Math.random() * 900000).toString();
    const payload = {
      prenom: form.prenom.trim(),
      email: form.email.trim(),
      telephone: form.telephone.trim(),
      identiteNumerique: "",
      source: "google-ads",
      code,
    };
    await submitLead(payload);
    setLoading(false);
    navigate("/merci", { state: { code } });
  };

  const inputClass = (field: string) =>
    `w-full rounded-lg border px-4 py-3 text-foreground outline-none transition focus:ring-2 focus:ring-primary/30 ${
      errors[field] ? "border-destructive bg-destructive/5" : "border-input bg-background"
    }`;

  return (
    <section className="relative overflow-hidden pt-20" style={{ background: "var(--hero-gradient)" }}>
      <div className="container mx-auto px-4 py-12 md:py-20">
        {/* Trust badges */}
        <div className="mb-8 flex flex-wrap items-center justify-center gap-3 text-sm font-medium md:justify-start">
          <span className="inline-flex items-center gap-1 rounded-full border border-primary/20 bg-primary/10 px-3 py-1 text-primary">
            <CheckCircle className="h-4 w-4" /> Certifié Qualiopi
          </span>
          <span className="inline-flex items-center gap-1 rounded-full border border-primary/20 bg-primary/10 px-3 py-1 text-primary">
            💶 100% financé par votre CPF
          </span>
          <span className="inline-flex items-center gap-1 rounded-full border border-primary/20 bg-primary/10 px-3 py-1 text-primary">
            ⭐ Note moyenne 4,8/5
          </span>
        </div>

        <div className="grid items-center gap-10 lg:grid-cols-2 lg:gap-16">
          {/* Left: Copy + image */}
          <div className="text-center lg:text-left">
            <h1 className="mb-6 text-3xl font-extrabold leading-tight tracking-tight md:text-4xl lg:text-5xl">
              Faites le Point sur Votre Carrière avec un Bilan de Compétences{" "}
              <span className="text-primary">100% financé par votre CPF</span>
            </h1>
            <p className="mb-8 text-lg text-muted-foreground md:text-xl">
              Sans avance de frais — Votre CPF prend en charge l'intégralité du bilan.
            </p>
            <img
              src={heroImage}
              alt="Professionnelle épanouie lors d'un bilan de compétences en ligne"
              className="mx-auto hidden w-full max-w-md rounded-2xl shadow-lg lg:block"
              width={800}
              height={600}
              loading="eager"
              decoding="async"
              fetchPriority="high"
            />
          </div>

          {/* Right: Form */}
          <div id="hero-form" className="scroll-mt-20">
            <div className="rounded-2xl border border-border bg-card p-6 shadow-2xl md:p-8">
              <h2 className="mb-6 text-center text-xl font-bold">
                Vérifiez votre éligibilité CPF
              </h2>
              <form onSubmit={handleSubmit} className="space-y-4">
                <input
                  type="text"
                  name="prenom"
                  placeholder="Prénom"
                  required
                  value={form.prenom}
                  onChange={handleChange}
                  className={inputClass("prenom")}
                />
                {errors.prenom && <p className="text-xs text-destructive -mt-2">{errors.prenom}</p>}
                <input
                  type="email"
                  name="email"
                  placeholder="Email"
                  required
                  value={form.email}
                  onChange={handleChange}
                  className={inputClass("email")}
                />
                {errors.email && <p className="text-xs text-destructive -mt-2">{errors.email}</p>}
                <input
                  type="tel"
                  name="telephone"
                  placeholder="Téléphone (ex: 06 12 34 56 78)"
                  required
                  value={form.telephone}
                  onChange={handleChange}
                  className={inputClass("telephone")}
                />
                {errors.telephone && <p className="text-xs text-destructive -mt-2">{errors.telephone}</p>}
                <button type="submit" disabled={loading} className="btn-cta w-full text-base md:text-lg">
                  {loading ? "Envoi en cours..." : "Vérifier mon éligibilité gratuitement"}
                </button>
                <p className="text-center text-xs text-muted-foreground">
                  Réponse sous 24h • 100% confidentiel • Sans engagement
                </p>
              </form>
              <p className="mt-4 flex items-center justify-center gap-1.5 text-xs text-muted-foreground">
                <Shield className="h-3.5 w-3.5" /> Vos données sont confidentielles.
              </p>
            </div>
          </div>
        </div>

        {/* Mobile hero image */}
        <img
          src={heroImage}
          alt="Professionnelle épanouie lors d'un bilan de compétences en ligne"
          className="mx-auto mt-10 w-full max-w-sm rounded-2xl shadow-lg lg:hidden"
          width={800}
          height={600}
          loading="lazy"
          decoding="async"
        />
      </div>
    </section>
  );
};

export default HeroSection;
